import React, { useState, useRef, useEffect } from 'react';
import { Download, ChevronDown, FileImage, FileText, Loader2 } from 'lucide-react';
import { jsPDF } from 'jspdf';

const DOWNLOAD_FORMATS = [
  { id: 'png', label: 'PNG', description: 'Lossless, transparent', icon: FileImage },
  { id: 'jpeg', label: 'JPEG', description: 'Smaller size, no transparency', icon: FileImage },
  { id: 'webp', label: 'WebP', description: 'Modern format, best compression', icon: FileImage },
  { id: 'pdf', label: 'PDF', description: 'Document format, printable', icon: FileText },
];

const DownloadFormatDropdown = ({
  imageUrl,
  filename = 'download',
  buttonStyle = {},
  buttonClassName = 'btn btn-primary',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadImageToCanvas = async (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve({ canvas, img });
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  const canvasToBlob = (canvas, format, quality = 0.92) => {
    return new Promise((resolve) => {
      const mimeType = format === 'jpeg' ? 'image/jpeg' :
                       format === 'webp' ? 'image/webp' :
                       'image/png';
      canvas.toBlob(resolve, mimeType, quality);
    });
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownload = async (format) => {
    if (!imageUrl || isDownloading) return;

    setIsDownloading(true);
    setDownloadingFormat(format);
    setIsOpen(false);

    try {
      const sanitizedFilename = filename.replace(/[^a-zA-Z0-9-_\s]/g, '').trim() || 'download';

      if (format === 'pdf') {
        const { canvas, img } = await loadImageToCanvas(imageUrl);
        const imgWidth = img.naturalWidth;
        const imgHeight = img.naturalHeight;
        const orientation = imgWidth > imgHeight ? 'landscape' : 'portrait';
        const pxToMm = 0.264583;
        const pdfWidth = imgWidth * pxToMm;
        const pdfHeight = imgHeight * pxToMm;

        const pdf = new jsPDF({
          orientation,
          unit: 'mm',
          format: [pdfWidth, pdfHeight]
        });

        const imgData = canvas.toDataURL('image/png');
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`${sanitizedFilename}.pdf`);
      } else {
        const { canvas } = await loadImageToCanvas(imageUrl);

        if (format === 'jpeg') {
          const jpegCanvas = document.createElement('canvas');
          jpegCanvas.width = canvas.width;
          jpegCanvas.height = canvas.height;
          const jpegCtx = jpegCanvas.getContext('2d');
          jpegCtx.fillStyle = '#FFFFFF';
          jpegCtx.fillRect(0, 0, jpegCanvas.width, jpegCanvas.height);
          jpegCtx.drawImage(canvas, 0, 0);
          const blob = await canvasToBlob(jpegCanvas, format);
          downloadBlob(blob, `${sanitizedFilename}.jpg`);
        } else {
          const blob = await canvasToBlob(canvas, format);
          const extension = format === 'webp' ? 'webp' : 'png';
          downloadBlob(blob, `${sanitizedFilename}.${extension}`);
        }
      }
    } catch (error) {
      console.error('Download failed:', error);
      window.open(imageUrl, '_blank');
    } finally {
      setIsDownloading(false);
      setDownloadingFormat(null);
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', flex: 1 }}>
      <button
        className={buttonClassName}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-2)',
          ...buttonStyle
        }}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isDownloading}
      >
        {isDownloading ? (
          <>
            <Loader2 size={18} className="spin" />
            Downloading...
          </>
        ) : (
          <>
            <Download size={18} />
            Download
            <ChevronDown size={16} style={{ marginLeft: 'auto' }} />
          </>
        )}
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: 0,
          right: 0,
          marginBottom: 'var(--space-2)',
          backgroundColor: 'var(--color-bg-primary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
          zIndex: 1001
        }}>
          <div style={{
            padding: 'var(--space-2) var(--space-3)',
            borderBottom: '1px solid var(--color-border)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-tertiary)',
            fontWeight: 500
          }}>
            Choose Format
          </div>
          {DOWNLOAD_FORMATS.map((format) => {
            const Icon = format.icon;
            return (
              <button
                key={format.id}
                onClick={() => handleDownload(format.id)}
                disabled={isDownloading}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3)',
                  background: 'transparent',
                  border: 'none',
                  cursor: isDownloading ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  transition: 'background-color 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: format.id === 'pdf' ? 'var(--error-100)' : 'var(--primary-100)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: format.id === 'pdf' ? 'var(--error-600)' : 'var(--primary-600)'
                }}>
                  <Icon size={18} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: 600,
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-primary)'
                  }}>
                    {format.label}
                  </div>
                  <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)'
                  }}>
                    {format.description}
                  </div>
                </div>
                {downloadingFormat === format.id && (
                  <Loader2 size={16} className="spin" style={{ color: 'var(--primary-500)' }} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DownloadFormatDropdown;
